/**
 * Submit Title IMDB Module
 * First step in submission process - initializes localStorage session
 * Fetches movie data from IMDB, shows editable details, and stores in localStorage
 */
(function () {
  "use strict";

  // Run once per page: a second copy binds Confirm again and saves step 1
  // twice (see submission-session-manager.js).
  if (window.submitTitleImdbLoaded) {
    return;
  }
  window.submitTitleImdbLoaded = true;

  const sessionManager = window.SubmissionSessionManager;

  let elements = {};
  let currentMovieData = null;
  let currentImdbId = null;

  function getApplyBasePath() {
    return window.submissionApplyBasePath || "/partners/bloodstream/apply";
  }

  function init() {
    if (!sessionManager) {
      console.error("Session manager not loaded");
      return;
    }

    const container = document.querySelector("#submit-title-imdb");
    if (!container) {
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const urlSessionId = urlParams.get("session_id");
    const sessionSubmitted = urlParams.get("session_submitted");

    if (sessionSubmitted === "1") {
      sessionManager.clearSession();
      window.location.href = window.location.pathname;
      return;
    }

    if (!urlSessionId) {
      sessionManager.clearSession();
    } else {
      if (sessionManager.isSubmitted(urlSessionId)) {
        sessionManager.clearSession();
        window.location.href = window.location.pathname;
        return;
      }
    }

    sessionManager.initializeSession();
    sessionManager.setCurrentStep(1);

    elements = {
      imdbUrlInput: container.querySelector("#imdb-url"),
      lookupBtn: container.querySelector("#lookupFilm"),
      loadingDiv: container.querySelector("#lookupFilm .loading"),
      normalDiv: container.querySelector("#lookupFilm .normal"),
      devNextBtn: container.querySelector("#dev-next-step"),
      errorGeneral: container.querySelector("#imdb-error-general"),
      errorMessageText: container.querySelector(".error-message-text"),
      // Details section (replaces old countdown)
      detailsSection: container.querySelector("#imdb-details"),
      moviePoster: container.querySelector("#movie-poster"),
      posterContainer: container.querySelector("#edit-poster-container"),
      editTitle: container.querySelector("#edit-title"),
      editEmail: container.querySelector("#edit-email"),
      editYear: container.querySelector("#edit-year"),
      editGenres: container.querySelector("#edit-genres"),
      editDirector: container.querySelector("#edit-director"),
      editDescription: container.querySelector("#edit-description"),
      changeFilmBtn: container.querySelector("#change-film"),
      confirmContinueBtn: container.querySelector("#confirm-continue"),
      contentTypeNotice: container.querySelector("#content-type-notice"),
      contentTypeNoticeText: container.querySelector("#content-type-notice-text"),
    };

    bindEvents();
    restoreFormData();
  }

  function bindEvents() {
    if (elements.devNextBtn) {
      elements.devNextBtn.addEventListener("click", (e) => {
        e.preventDefault();
        sessionManager.navigateWithSession(`${getApplyBasePath()}/upload`);
      });
    }

    const devSkipCustomize = document.querySelector("#dev-skip-customize");
    if (devSkipCustomize) {
      devSkipCustomize.addEventListener("click", (e) => {
        e.preventDefault();
        sessionManager.navigateWithSession(`${getApplyBasePath()}/customize`);
      });
    }

    const devSkipDistribution = document.querySelector("#dev-skip-distribution");
    if (devSkipDistribution) {
      devSkipDistribution.addEventListener("click", (e) => {
        e.preventDefault();
        sessionManager.navigateWithSession(`${getApplyBasePath()}/distribution`);
      });
    }

    const devSkipReview = document.querySelector("#dev-skip-review");
    if (devSkipReview) {
      devSkipReview.addEventListener("click", (e) => {
        e.preventDefault();
        sessionManager.navigateWithSession(`${getApplyBasePath()}/review`);
      });
    }

    if (elements.lookupBtn) {
      elements.lookupBtn.addEventListener("click", (e) => {
        e.preventDefault();
        lookupFilm();
      });
    }

    if (elements.imdbUrlInput) {
      elements.imdbUrlInput.addEventListener("keypress", (e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          lookupFilm();
        }
      });

      elements.imdbUrlInput.addEventListener("blur", (e) => {
        const value = e.target.value.trim();
        if (value && value.match(/^tt\d+$/i)) {
          e.target.value = `https://www.imdb.com/title/${value}/`;
        }
      });

      elements.imdbUrlInput.addEventListener("paste", (e) => {
        setTimeout(() => {
          const value = e.target.value.trim();
          if (value && extractImdbId(value)) {
            lookupFilm();
          }
        }, 100);
      });
    }

    // Content type radio change
    document.querySelectorAll('input[name="content_type"]').forEach((radio) => {
      radio.addEventListener("change", handleContentTypeChange);
    });

    // Change Film button
    if (elements.changeFilmBtn) {
      elements.changeFilmBtn.addEventListener("click", (e) => {
        e.preventDefault();
        resetToLookup();
      });
    }

    // Confirm & Continue button
    if (elements.confirmContinueBtn) {
      elements.confirmContinueBtn.addEventListener("click", (e) => {
        e.preventDefault();
        confirmAndContinue();
      });
    }
  }

  function extractImdbId(url) {
    const match = url.match(/(?:imdb\.com\/title\/)?(tt\d+)/i);
    return match ? match[1] : null;
  }

  function restoreFormData() {
    const step1Data = sessionManager.getStepData(1);

    if (step1Data.imdb_id && elements.imdbUrlInput) {
      elements.imdbUrlInput.value = `https://www.imdb.com/title/${step1Data.imdb_id}/`;

      // If we have full saved data, restore the details form
      if (step1Data.title_name) {
        currentImdbId = step1Data.imdb_id;
        currentMovieData = step1Data.imdb_data || null;
        showDetails(step1Data);
      }
    }

    if (step1Data.email && elements.editEmail) {
      elements.editEmail.value = step1Data.email;
    }
  }

  function lookupFilm() {
    const imdbUrl = elements.imdbUrlInput?.value.trim();

    if (!imdbUrl) {
      showGeneralError("Please enter an IMDB URL");
      return;
    }

    const imdbId = extractImdbId(imdbUrl);
    if (!imdbId) {
      showGeneralError(
        "Please enter a valid IMDB URL (e.g., https://www.imdb.com/title/tt1234567/)"
      );
      return;
    }

    clearError();
    fetchImdbData(imdbId);
  }

  function fetchImdbData(imdbId) {
    setLoadingState(true);
    clearError();

    fetch(
      "/api/imdb?" +
        new URLSearchParams({
          imdb: imdbId,
        })
    )
      .then((response) => response.json())
      .then((data) => {
        setLoadingState(false);

        if (!data.success) {
          showGeneralError(
            data.message || "Failed to fetch IMDB data. Please try again."
          );
          return;
        }

        const metadata = data.data?.metadata || {};
        const actualImdbId = data.data?.id || data.data?.imdb_id || imdbId;

        currentMovieData = data.data;
        currentImdbId = actualImdbId;

        // Extract fields from IMDB data
        const contentType = metadata["@type"] || metadata.type || "";
        const movieTitle = metadata.name || metadata.title || "";
        const year =
          metadata.year || metadata.datePublished?.substr(0, 4) || "";
        const genres = metadata.genre || [];
        const genresStr = Array.isArray(genres) ? genres.join(", ") : genres;
        const posterUrl = data.data.posterUrl || "";
        const description = metadata.description || "";

        // Extract director from credits
        let director = "";
        const credits = data.data.credits || [];
        if (Array.isArray(credits)) {
          const directorCredit = credits.find(
            (c) =>
              c.category === "Director" ||
              c.category === "Directed by" ||
              c.role === "director"
          );
          if (directorCredit) {
            director = directorCredit.name || "";
          }
        }

        // Auto-select content type based on IMDB type
        let autoContentType = "feature";
        if (contentType === "TVSeries" || contentType === "TVMiniSeries") {
          autoContentType = "episodic";
        }

        const detailsData = {
          title_name: movieTitle,
          year: year,
          genres: genresStr,
          director: director,
          description: description,
          poster_url: posterUrl,
          content_type: autoContentType,
          imdb_content_type: contentType,
        };

        showDetails(detailsData);
      })
      .catch((error) => {
        setLoadingState(false);
        console.error("IMDB lookup error:", error);
        showGeneralError(
          "An error occurred while fetching IMDB data. Please try again."
        );
      });
  }

  /**
   * Show the editable details form populated with data
   */
  function showDetails(data) {
    if (!elements.detailsSection) return;

    // Populate editable fields
    if (elements.editTitle) {
      const decodedTitle = window.HtmlEntityDecoder
        ? window.HtmlEntityDecoder.decode(data.title_name || "")
        : data.title_name || "";
      elements.editTitle.value = decodedTitle;
    }
    if (elements.editYear) elements.editYear.value = data.year || "";
    if (elements.editGenres) elements.editGenres.value = data.genres || "";
    if (elements.editDirector)
      elements.editDirector.value = data.director || "";
    if (elements.editDescription)
      elements.editDescription.value = data.description || "";

    // Show poster
    if (elements.moviePoster && data.poster_url) {
      elements.moviePoster.src = data.poster_url;
      if (elements.posterContainer) {
        elements.posterContainer.classList.remove("hide");
      }
    }

    // Set content type radio
    const contentType = data.content_type || "feature";
    const radio = document.querySelector(
      `input[name="content_type"][value="${contentType}"]`
    );
    if (radio) {
      radio.checked = true;
      handleContentTypeChange();
    }

    // Show details, hide lookup area
    elements.detailsSection.classList.remove("hide");

    // Dim the lookup area to indicate it's been used
    const lookupRow = elements.imdbUrlInput?.closest(
      ".grid-x.grid-margin-x-small"
    );
    if (lookupRow) {
      lookupRow.style.opacity = "0.5";
      lookupRow.style.pointerEvents = "none";
    }
  }

  /**
   * Handle content type radio change
   */
  function handleContentTypeChange() {
    const selected = document.querySelector(
      'input[name="content_type"]:checked'
    );
    if (!selected) return;

    const value = selected.value;
    const notice = elements.contentTypeNotice;
    const noticeText = elements.contentTypeNoticeText;
    const continueBtn = elements.confirmContinueBtn;

    if (value === "episodic") {
      if (noticeText)
        noticeText.textContent =
          "Episodic / series submissions require additional setup. You can continue with self-service, or contact our team for assistance with multi-episode delivery.";
      if (notice) notice.classList.remove("hide");
      if (continueBtn) continueBtn.disabled = false;
    } else if (value === "standalone_trailer") {
      if (noticeText)
        noticeText.textContent =
          "Trailer-only submissions are handled by our distribution team. Please contact us to discuss placement options.";
      if (notice) notice.classList.remove("hide");
      if (continueBtn) continueBtn.disabled = true;
    } else if (value === "other") {
      if (noticeText)
        noticeText.textContent =
          "For documentaries, specials, music videos, and other content types, please contact our distribution team for a custom submission workflow.";
      if (notice) notice.classList.remove("hide");
      if (continueBtn) continueBtn.disabled = true;
    } else {
      // feature
      if (notice) notice.classList.add("hide");
      if (continueBtn) continueBtn.disabled = false;
    }
  }

  /**
   * Reset back to the IMDB lookup state
   */
  function resetToLookup() {
    currentMovieData = null;
    currentImdbId = null;

    // Hide details
    if (elements.detailsSection) elements.detailsSection.classList.add("hide");

    // Re-enable lookup area
    const lookupRow = elements.imdbUrlInput?.closest(
      ".grid-x.grid-margin-x-small"
    );
    if (lookupRow) {
      lookupRow.style.opacity = "";
      lookupRow.style.pointerEvents = "";
    }

    // Clear and focus input
    if (elements.imdbUrlInput) {
      elements.imdbUrlInput.value = "";
      elements.imdbUrlInput.focus();
    }

    clearError();
  }

  /**
   * Confirm details and continue to next step
   */
  async function confirmAndContinue() {
    // Validate title is filled
    const title = elements.editTitle?.value.trim();
    if (!title) {
      showGeneralError("Please enter a title for your film.");
      elements.editTitle?.focus();
      return;
    }

    const email = elements.editEmail?.value.trim() || "";
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      showGeneralError(
        "Please enter a valid email address so we can send you your review result."
      );
      elements.editEmail?.focus();
      return;
    }

    const contentType =
      document.querySelector('input[name="content_type"]:checked')?.value ||
      "feature";

    // Block non-self-service types
    if (contentType === "standalone_trailer" || contentType === "other") {
      return;
    }

    // Gather the (possibly edited) data
    const posterUrl = elements.moviePoster?.src || "";
    const step1Data = {
      imdb_id: currentImdbId,
      imdb_data: currentMovieData,
      content_type: contentType,
      title_name: title,
      email: email,
      poster_url: posterUrl,
      year: elements.editYear?.value.trim() || "",
      genres: elements.editGenres?.value.trim() || "",
      director: elements.editDirector?.value.trim() || "",
      description: elements.editDescription?.value.trim() || "",
      metadata: currentMovieData?.metadata || {},
      theme: window.submissionThemeKey || "bloodstream",
      fetched_at: new Date().toISOString(),
    };

    sessionManager.saveStepData(1, step1Data);
    sessionManager.markStepCompleted(1);

    // Disable button while saving
    if (elements.confirmContinueBtn) {
      elements.confirmContinueBtn.disabled = true;
      elements.confirmContinueBtn.innerHTML =
        '<div class="spinner small" style="display:inline-block;"></div> Saving...';
    }

    try {
      await persistStepData(1, step1Data);
      sessionManager.navigateWithSession(`${getApplyBasePath()}/upload`);
    } catch (error) {
      console.error("[Step 1] Failed to persist to database:", error);
      showGeneralError("Failed to save your data. Please try again.");
      if (elements.confirmContinueBtn) {
        elements.confirmContinueBtn.disabled = false;
        elements.confirmContinueBtn.innerHTML =
          'Confirm & Continue <i class="fas fa-arrow-right"></i>';
      }
    }
  }

  function setLoadingState(isLoading) {
    if (!elements.lookupBtn) return;

    if (isLoading) {
      elements.lookupBtn.disabled = true;
      if (elements.loadingDiv) elements.loadingDiv.classList.remove("hide");
      if (elements.normalDiv) elements.normalDiv.classList.add("hide");
    } else {
      elements.lookupBtn.disabled = false;
      if (elements.loadingDiv) elements.loadingDiv.classList.add("hide");
      if (elements.normalDiv) elements.normalDiv.classList.remove("hide");
    }
  }

  function clearError() {
    if (elements.errorGeneral) elements.errorGeneral.classList.add("hide");
  }

  function showGeneralError(message) {
    clearError();
    if (elements.errorGeneral && elements.errorMessageText) {
      elements.errorMessageText.textContent = message;
      elements.errorGeneral.classList.remove("hide");
      elements.errorGeneral.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }

  async function persistStepData(stepNumber, formData) {
    const response = await fetch("/api/submissions/save-step", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        uuid: sessionManager.getSessionId(),
        step: stepNumber,
        form_data: formData,
      }),
    });

    const result = await response.json();
    if (!result.success) {
      throw new Error(result.message || "Database persistence failed");
    }

    return true;
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
